<template>
  <div class="page">
    <div class="seiten-kopf">
      <router-link to="/rezepte/liste" class="seiten-kopf-zurueck" aria-label="Zurück">
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
      </router-link>
      <h1>Rezept bearbeiten</h1>
    </div>

    <form class="rezept-form" @submit.prevent>
      <label for="rezept-name">Rezeptname</label>
      <div class="input-field">
        <input id="rezept-name" v-model="Rezept.Name" type="text" placeholder="Rezeptname" />
      </div>

      <label>Kategorie(n)</label>
      <div class="filter-chip-reihe">
        <button
          v-for="Kat in KategorieOptionen"
          :key="Kat"
          type="button"
          class="filter-chip"
          :class="{ 'filter-chip--aktiv': Rezept.Kategorien.includes(Kat) }"
          @click="KategorieToggeln(Kat)"
        >
          {{ Kat }}
        </button>
      </div>

      <label for="rezept-personen">Für wie viele Personen</label>
      <div class="input-field personen-feld">
        <font-awesome-icon icon="fa-solid fa-users" />
        <input id="rezept-personen" v-model.number="Rezept.Personen" type="number" min="1" max="99" step="1" />
      </div>

      <label class="filter-toggle-zeile">
        <input v-model="Rezept.Eingefroren" type="checkbox" />
        🧊 Dieses Rezept ist eingefroren
      </label>

      <BeschreibungsFeld v-model="Rezept.Beschreibung" @uebernommen="BeschreibungUebernommen" />

      <label>Foto</label>
      <BildAuswahl v-model="Bild" :bestehend-url="BestehendesBildUrl" />

      <div class="zutaten-header">
        <span>Zutaten</span>
        <button type="button" class="plus-button" @click="ModalOeffnen" aria-label="Zutaten auswählen">
          <font-awesome-icon icon="fa-solid fa-plus" />
        </button>
        <button type="button" class="neu-zutat-btn" @click="NeuZutatOeffnen">
          <font-awesome-icon icon="fa-solid fa-seedling" /> Neue Zutat
        </button>
      </div>

      <p v-if="Zutaten.length === 0" class="hint-message">
        Noch keine Zutaten vorhanden. Lege zuerst welche in der Zutatenliste an.
      </p>
      <p v-else-if="Rezept.Zutaten.length === 0" class="hint-message">
        Noch keine Zutaten ausgewählt.
      </p>

      <div class="zutat-zeile" v-for="(Zutat, Index) in Rezept.Zutaten" :key="Zutat.ZutatId">
        <span class="zutat-name-text">{{ ZutatNameFinden(Zutat.ZutatId) }}</span>
        <input v-model.number="Zutat.Menge" type="number" min="0" step="any" placeholder="Menge" class="zutat-menge" />
        <select v-model="Zutat.Einheit" class="zutat-einheit">
          <option value="Gramm">Gramm</option>
          <option value="Kg">Kg</option>
          <option value="ml">ml</option>
          <option value="Liter">Liter</option>
          <option value="Volumen">Volumen</option>
          <option value="Stück">Stück</option>
          <option value="Flasche">Flasche</option>
          <option value="Packung">Packung</option>
          <option value="EL">EL</option>
          <option value="Netz">Netz</option>
        </select>
        <button type="button" class="entfernen-button" @click="ZutatEntfernen(Index)" aria-label="Zutat entfernen">
          <font-awesome-icon icon="fa-solid fa-trash" />
        </button>
      </div>

      <p v-if="Fehlermeldung" class="error-message">{{ Fehlermeldung }}</p>

      <div class="button-row">
        <button type="button" class="btn btn-primary" @click="Speichern">Speichern</button>
        <router-link to="/rezepte/liste" class="btn btn-secondary">Abbrechen</router-link>
      </div>
    </form>

    <Teleport to="body">
      <div v-if="ModalOffen" class="modal-overlay" @click.self="ModalSchliessen">
        <div class="modal-sheet">
          <div class="modal-kopf">
            <span class="modal-titel">Zutaten auswählen</span>
            <button class="modal-schliessen" @click="ModalSchliessen" aria-label="Schließen">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-suche">
            <input v-model="Suche" type="text" placeholder="Suchen..." class="suche-input" />
          </div>

          <div class="kacheln-grid" v-if="GefilterteZutaten.length > 0">
            <button
              v-for="Zutat in GefilterteZutaten"
              :key="Zutat.Id"
              type="button"
              class="kachel"
              :class="{ 'kachel--ausgewaehlt': AusgewaehlteIds.includes(Zutat.Id) }"
              @click="KachelToggeln(Zutat.Id)"
            >
              <font-awesome-icon
                v-if="AusgewaehlteIds.includes(Zutat.Id)"
                icon="fa-solid fa-check"
                class="kachel-check"
              />
              {{ Zutat.Name }}
            </button>
          </div>
          <div v-else class="kacheln-kein-treffer">
            <p class="hint-message">
              {{ Zutaten.length === 0 ? 'Noch keine Zutaten vorhanden.' : 'Keine Zutat gefunden.' }}
            </p>
            <button
              v-if="Suche.trim()"
              type="button"
              class="btn btn-secondary"
              @click="NeueZutatAusSucheOeffnen"
            >
              <span class="btn-inhalt">
                <font-awesome-icon icon="fa-solid fa-plus" />
                „{{ Suche.trim() }}“ anlegen &amp; hinzufügen
              </span>
            </button>
          </div>

          <div class="modal-fuss">
            <button type="button" class="btn btn-primary btn-breit" @click="AuswahlUebernehmen">
              Übernehmen ({{ AusgewaehlteIds.length }})
            </button>
            <button type="button" class="btn btn-secondary btn-breit" @click="NeuZutatOeffnen">
              <span class="btn-inhalt"><font-awesome-icon icon="fa-solid fa-plus" /> Neue Zutat anlegen</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="NeuZutatOffen" class="modal-overlay" @click.self="NeuZutatSchliessen">
        <div class="modal-sheet">
          <div class="modal-kopf">
            <span class="modal-titel">Neue Zutat anlegen</span>
            <button class="modal-schliessen" @click="NeuZutatSchliessen" aria-label="Schließen">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="neu-zutat-form">
            <label class="neu-zutat-label">Name *</label>
            <div class="input-field">
              <input v-model="NeuZutat.Name" type="text" placeholder="Zutatname" />
            </div>
            <label class="neu-zutat-label">Kategorie</label>
            <div class="input-field">
              <select v-model="NeuZutat.KategorieId">
                <option :value="null">– keine –</option>
                <option v-for="Kat in Kategorien" :key="Kat.Id" :value="Kat.Id">{{ Kat.Name }}</option>
              </select>
            </div>
            <div class="neu-zutat-menge-zeile">
              <div class="input-field neu-zutat-menge">
                <input v-model.number="NeuZutat.Menge" type="number" min="0" step="any" placeholder="Menge" />
              </div>
              <div class="input-field neu-zutat-einheit">
                <select v-model="NeuZutat.Einheit">
                  <option value="Gramm">Gramm</option>
                  <option value="Kg">Kg</option>
                  <option value="ml">ml</option>
                  <option value="Liter">Liter</option>
                  <option value="Volumen">Volumen</option>
                  <option value="Stück">Stück</option>
                  <option value="Flasche">Flasche</option>
                  <option value="Packung">Packung</option>
                  <option value="EL">EL</option>
                  <option value="Netz">Netz</option>
                </select>
              </div>
            </div>
            <p v-if="NeuZutatFehler" class="error-message">{{ NeuZutatFehler }}</p>
          </div>
          <div class="modal-fuss">
            <button type="button" class="btn btn-primary btn-breit" @click="NeuZutatSpeichern" :disabled="NeuZutatLaedt">
              Zutat speichern
            </button>
            <button type="button" class="btn btn-secondary btn-breit" @click="NeuZutatSchliessen">
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Zutaten, ZutatenLaden, Kategorien, KategorienLaden, ZutatHinzufuegen as NeueZutatAnlegen } from '../composables/useZutaten'
import { RezeptFinden, RezepteLaden, RezeptAktualisieren, RezeptBildSpeichern } from '../composables/useRezepte'
import { BildUrl } from '../composables/useBilder'
import BildAuswahl from '../components/BildAuswahl.vue'
import BeschreibungsFeld from '../components/BeschreibungsFeld.vue'

const KategorieOptionen = ['Frühstück', 'Vorspeise', 'Hauptgang', 'Nachspeise', 'Abendessen']

export default {
  name: 'RezepteBearbeitenView',
  components: { BildAuswahl, BeschreibungsFeld },
  data() {
    return {
      Zutaten,
      Kategorien,
      KategorieOptionen,
      RezeptId: null,
      Rezept: {
        Name: '',
        Kategorien: ['Hauptgang'],
        Personen: 4,
        Eingefroren: false,
        Beschreibung: '',
        Zutaten: []
      },
      Bild: undefined,
      Fehlermeldung: '',
      ModalOffen: false,
      Suche: '',
      AusgewaehlteIds: [],
      NeuZutatOffen: false,
      NeuZutat: { Name: '', KategorieId: null, Menge: null, Einheit: 'Gramm' },
      NeuZutatFehler: '',
      NeuZutatLaedt: false
    }
  },
  computed: {
    GefilterteZutaten() {
      const Suchtext = this.Suche.toLowerCase().trim()
      if (!Suchtext) return this.Zutaten
      return this.Zutaten.filter(z => z.Name.toLowerCase().includes(Suchtext))
    },
    BestehendesBildUrl() {
      const Rezept = RezeptFinden(this.RezeptId)
      return Rezept && Rezept.HatBild ? BildUrl('Rezept', Rezept.Id, Rezept.BildVersion) : null
    }
  },
  async created() {
    await Promise.all([ZutatenLaden(), KategorienLaden(), RezepteLaden()])
    this.RezeptId = Number(this.$route.params.id)
    const Rezept = RezeptFinden(this.RezeptId)
    if (Rezept) {
      this.Rezept = {
        Name: Rezept.Name,
        Kategorien: [...(Rezept.Kategorien || [])],
        Personen: Rezept.Personen || 4,
        Eingefroren: !!Rezept.Eingefroren,
        Beschreibung: Rezept.Beschreibung || '',
        Zutaten: Rezept.Zutaten.map(Zutat => ({
          ZutatId: Zutat.ZutatId,
          Menge: Zutat.Menge,
          Einheit: Zutat.Einheit
        }))
      }
    }
  },
  methods: {
    ModalOeffnen() {
      this.AusgewaehlteIds = this.Rezept.Zutaten.map(z => z.ZutatId)
      this.Suche = ''
      this.ModalOffen = true
    },
    ModalSchliessen() {
      this.ModalOffen = false
    },
    KachelToggeln(Id) {
      const Index = this.AusgewaehlteIds.indexOf(Id)
      if (Index === -1) {
        this.AusgewaehlteIds.push(Id)
      } else {
        this.AusgewaehlteIds.splice(Index, 1)
      }
    },
    AuswahlUebernehmen() {
      const BestehendeMap = new Map(this.Rezept.Zutaten.map(z => [z.ZutatId, z]))
      this.Rezept.Zutaten = this.AusgewaehlteIds.map(Id =>
        BestehendeMap.get(Id) || { ZutatId: Id, Menge: null, Einheit: 'Gramm' }
      )
      this.ModalOffen = false
    },
    ZutatEntfernen(Index) {
      this.Rezept.Zutaten.splice(Index, 1)
    },
    ZutatNameFinden(Id) {
      return this.Zutaten.find(z => z.Id === Id)?.Name ?? '?'
    },
    KategorieToggeln(Kat) {
      const Index = this.Rezept.Kategorien.indexOf(Kat)
      if (Index === -1) {
        this.Rezept.Kategorien.push(Kat)
      } else {
        this.Rezept.Kategorien.splice(Index, 1)
      }
    },
    async Speichern() {
      this.Fehlermeldung = ''
      if (!this.Rezept.Name.trim()) {
        this.Fehlermeldung = 'Rezeptname darf nicht leer sein.'
        return
      }
      if (this.Rezept.Kategorien.length === 0) {
        this.Fehlermeldung = 'Mindestens eine Kategorie muss ausgewählt sein.'
        return
      }
      if (this.Rezept.Zutaten.some(Zutat => !Zutat.ZutatId)) {
        this.Fehlermeldung = 'Bitte für jede Zutat eine Auswahl treffen oder die Zeile entfernen.'
        return
      }
      if (!Number.isInteger(this.Rezept.Personen) || this.Rezept.Personen < 1 || this.Rezept.Personen > 99) {
        this.Fehlermeldung = 'Personenanzahl muss zwischen 1 und 99 liegen.'
        return
      }
      try {
        await RezeptAktualisieren(this.RezeptId, {
          Name: this.Rezept.Name.trim(),
          Kategorien: this.Rezept.Kategorien,
          Personen: this.Rezept.Personen,
          Eingefroren: this.Rezept.Eingefroren,
          Beschreibung: this.Rezept.Beschreibung.trim() || null,
          Zutaten: this.Rezept.Zutaten.map(Zutat => ({
            ZutatId: Zutat.ZutatId,
            Menge: Zutat.Menge,
            Einheit: Zutat.Einheit
          }))
        })
        if (this.Bild !== undefined) {
          await RezeptBildSpeichern(this.RezeptId, this.Bild)
        }
        this.$router.push('/rezepte/liste')
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
      }
    },
    async BeschreibungUebernommen(NeuerWert) {
      // Rezept existiert schon (Bearbeiten-Ansicht) - Beschreibung sofort speichern,
      // statt auf den separaten Speichern-Button der Gesamtseite zu warten.
      this.Fehlermeldung = ''
      try {
        await RezeptAktualisieren(this.RezeptId, { Beschreibung: NeuerWert.trim() || null })
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
      }
    },
    NeuZutatOeffnen() {
      this.NeuZutat = { Name: '', KategorieId: null, Menge: null, Einheit: 'Gramm' }
      this.NeuZutatFehler = ''
      this.NeuZutatOffen = true
    },
    NeueZutatAusSucheOeffnen() {
      this.NeuZutat = { Name: this.Suche.trim(), KategorieId: null, Menge: null, Einheit: 'Gramm' }
      this.NeuZutatFehler = ''
      this.NeuZutatOffen = true
    },
    NeuZutatSchliessen() {
      this.NeuZutatOffen = false
    },
    async NeuZutatSpeichern() {
      this.NeuZutatFehler = ''
      if (!this.NeuZutat.Name.trim()) {
        this.NeuZutatFehler = 'Name darf nicht leer sein.'
        return
      }
      this.NeuZutatLaedt = true
      try {
        const Neu = await NeueZutatAnlegen({
          Name: this.NeuZutat.Name.trim(),
          KategorieId: this.NeuZutat.KategorieId || null,
          Menge: this.NeuZutat.Menge || null,
          Einheit: this.NeuZutat.Einheit
        })
        this.Rezept.Zutaten.push({ ZutatId: Neu.Id, Menge: this.NeuZutat.Menge || null, Einheit: this.NeuZutat.Einheit })
        if (!this.AusgewaehlteIds.includes(Neu.Id)) {
          this.AusgewaehlteIds.push(Neu.Id)
        }
        this.NeuZutatOffen = false
        this.Suche = ''
      } catch (Fehler) {
        this.NeuZutatFehler = Fehler.message
      } finally {
        this.NeuZutatLaedt = false
      }
    }
  }
}
</script>

<style scoped>
.rezept-form {
  display: flex;
  flex-direction: column;
  max-width: 480px;
  margin-top: 16px;
}

.rezept-form label {
  margin-top: 16px;
  margin-bottom: 4px;
  font-size: 0.9rem;
}

.personen-feld {
  max-width: 160px;
}

.rezept-form .input-field select {
  flex: 1;
  border: none;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 1rem;
  outline: none;
}

.zutaten-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  font-size: 0.9rem;
}

.plus-button {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background-color: var(--color-bg-alt);
  color: var(--color-primary);
  cursor: pointer;
}

.zutat-zeile {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  align-items: center;
}

.zutat-name-text {
  flex: 2;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background-color: var(--color-bg-alt);
  font-size: 1rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.zutat-menge {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 1rem;
  min-width: 0;
}

.zutat-einheit {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 1rem;
  min-width: 0;
}

.entfernen-button {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background-color: var(--color-bg-alt);
  color: #e74c3c;
  cursor: pointer;
  flex-shrink: 0;
}

@media (max-width: 480px) {
  .zutat-name-text {
    flex-basis: 100%;
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  animation: Einblenden 0.25s ease both;
}

.modal-sheet {
  width: 100%;
  max-height: 90dvh;
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.modal-kopf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.modal-titel {
  font-size: 1.1rem;
  font-weight: 600;
}

.modal-schliessen {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-suche {
  padding: 12px 16px;
  flex-shrink: 0;
}

.suche-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 1rem;
  box-sizing: border-box;
}

.kacheln-grid {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 8px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 8px;
  align-content: start;
}

.kacheln-kein-treffer {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 16px;
}

.kachel {
  padding: 12px 8px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 0.95rem;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  word-break: break-word;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.kachel--ausgewaehlt {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.kachel-check {
  flex-shrink: 0;
}

.modal-fuss {
  padding: 16px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-breit {
  width: 100%;
}

.neu-zutat-btn {
  margin-left: auto;
  padding: 4px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg-alt);
  color: var(--color-primary);
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

.neu-zutat-btn:hover {
  border-color: var(--color-primary);
}

.neu-zutat-form {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.neu-zutat-label {
  font-size: 0.9rem;
  margin-top: 8px;
}

.neu-zutat-menge-zeile {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.neu-zutat-menge,
.neu-zutat-einheit {
  flex: 1;
}
</style>
